import { BillingEumEditType, EumChangeReasonCode } from "~/graphql/supergraphEnums";
import type {
  BulkActionChangeData,
  EumCalcEditChangeData,
  EumValueOverrideChangeData,
  PendingFCCChange,
} from "../types";
import type { CurrentValuesSnapshotInput, EumChangeItemInput } from "~/graphql/supergraphTypes";

const VALID_REASON_CODES: ReadonlyArray<string> = [
  EumChangeReasonCode.ClientDispute,
  EumChangeReasonCode.DataCorrection,
  EumChangeReasonCode.ContractAmendment,
  EumChangeReasonCode.Other,
];

const isEumChangeReasonCode = (value: string): value is EumChangeReasonCode =>
  VALID_REASON_CODES.includes(value);

const toDecimalStringFromDollars = (value: number | null): string | null => {
  if (value === null || Number.isNaN(value)) {
    return null;
  }
  return value.toFixed(2);
};

const toRequiredDecimalStringFromDollars = (value: number): string => value.toFixed(2);

const resolveReasonCode = (rawReasonCode: string): EumChangeReasonCode => {
  if (!isEumChangeReasonCode(rawReasonCode)) {
    throw new Error(`Unsupported EUM reason code: ${rawReasonCode}`);
  }
  return rawReasonCode;
};

const resolveSingleEntityId = (entityIds: number[]): number => {
  if (entityIds.length !== 1) {
    throw new Error(`EUM changes must target exactly one entity, received ${entityIds.length}`);
  }
  return entityIds[0];
};

export type BuildEumOverrideItemInput = {
  change: EumValueOverrideChangeData;
  s3DocumentKey: string | null;
  snapshotTakenAt: string;
};

export const buildEumOverrideItem = ({
  change,
  s3DocumentKey,
  snapshotTakenAt,
}: BuildEumOverrideItemInput): EumChangeItemInput => {
  const currentValuesSnapshot: CurrentValuesSnapshotInput = {
    snapshotTakenAt,
    effectiveEumValue: toDecimalStringFromDollars(change.currentEumValue),
    netCapitalValue: null,
    totalCommitmentValue: null,
    distributions: null,
  };

  return {
    entityId: resolveSingleEntityId(change.entityIds),
    changeType: BillingEumEditType.EumValueOverride,
    reasonCode: resolveReasonCode(change.reasonCode),
    justification: change.description,
    overridePayload: {
      eumOverrideValue: toRequiredDecimalStringFromDollars(change.proposedEumValue),
      s3DocumentKey,
    },
    currentValuesSnapshot,
  };
};

export type BuildEumCalcEditItemInput = {
  change: EumCalcEditChangeData;
  s3DocumentKey: string | null;
  snapshotTakenAt: string;
};

export const buildEumCalcEditItem = ({
  change,
  s3DocumentKey,
  snapshotTakenAt,
}: BuildEumCalcEditItemInput): EumChangeItemInput => {
  const currentValuesSnapshot: CurrentValuesSnapshotInput = {
    snapshotTakenAt,
    effectiveEumValue: toDecimalStringFromDollars(change.current.eumValue),
    netCapitalValue: toDecimalStringFromDollars(change.current.netCapitalValue),
    totalCommitmentValue: toDecimalStringFromDollars(change.current.totalCommitmentValue),
    distributions: toDecimalStringFromDollars(change.current.distributionsValue),
  };

  return {
    entityId: resolveSingleEntityId(change.entityIds),
    changeType: BillingEumEditType.EumCalcEdit,
    reasonCode: resolveReasonCode(change.reasonCode),
    justification: change.description,
    calcEditPayload: {
      totalCommitmentValue: toDecimalStringFromDollars(change.proposed.totalCommitmentValue),
      netCapitalValue: toDecimalStringFromDollars(change.proposed.netCapitalValue),
      distributions: toDecimalStringFromDollars(change.proposed.distributionsValue),
      s3DocumentKey,
    },
    currentValuesSnapshot,
  };
};

export const isEumPendingChange = (
  change: { action?: string } & object,
): change is EumValueOverrideChangeData | EumCalcEditChangeData =>
  "action" in change &&
  (change.action === "eum_value_override" || change.action === "eum_calc_edit");

export const wouldMixChangeTypes = (
  pendingChanges: BulkActionChangeData[],
  fccChanges: PendingFCCChange[],
  incomingIsEum: boolean,
): boolean => {
  const hasEum = pendingChanges.some(isEumPendingChange);
  const hasOther =
    pendingChanges.some((change) => !isEumPendingChange(change)) || fccChanges.length > 0;
  return incomingIsEum ? hasOther : hasEum;
};
